<template>
  <div class="orders-container">
    <el-card class="filter-card" shadow="hover">
      <el-form :inline="true" :model="filterForm" class="filter-form">
        <el-form-item label="订单编号">
          <el-input v-model="filterForm.orderSn" placeholder="请输入订单编号" clearable style="width: 220px"></el-input>
        </el-form-item>
        <el-form-item label="订单状态">
          <el-select
            v-model="filterForm.orderStatus"
            filterable
            clearable
            placeholder="请选择订单状态"
            style="width: 220px">
            <el-option
              v-for="(value, key) in statusOptions"
              :key="key"
              :label="value"
              :value="key">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="创建时间">
          <el-date-picker
            v-model="filterForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            style="width: 350px">
          </el-date-picker>
        </el-form-item>
        <div class="form-buttons-container">
          <el-form-item class="form-buttons">
            <el-button type="primary" @click="handleFilter" icon="el-icon-search">查询</el-button>
            <el-button @click="resetFilter" icon="el-icon-refresh">重置</el-button>
          </el-form-item>
        </div>
      </el-form>
    </el-card>

    <el-card class="table-card">
      <div class="table-header">
        <div class="header-left">
          <span class="title">{{ pageTitle }}</span>
          <span class="count">共 {{ totalItems }} 条</span>
        </div>
      </div>
      
      <el-table
        :data="orderList"
        border
        stripe
        highlight-current-row
        v-loading="tableLoading"
        :row-class-name="getRowClassName"
        style="width: 100%">
        <el-table-column
          prop="orderSn"
          label="订单编号"
          min-width="160"
          show-overflow-tooltip>
          <template slot-scope="scope">
            <div class="order-sn-cell">
              {{ scope.row.orderSn }}
              <template v-if="scope.row.isRetest === 1">
                <el-tag size="mini" type="warning" style="margin-left: 5px">复测</el-tag>
              </template>
              <template v-if="scope.row.isDispute === 1">
                <el-tag size="mini" type="danger" style="margin-left: 5px">争议</el-tag>
              </template>
              <template v-if="scope.row.isBackOrder === 1">
                <el-tag size="mini" type="info" style="margin-left: 5px">回寄</el-tag>
              </template>
              <template v-if="scope.row.isForward === 1">
                <el-tag size="mini" type="success" style="margin-left: 5px">转寄</el-tag>
              </template>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="projectName"
          label="项目名称"
          min-width="120"
          show-overflow-tooltip>
        </el-table-column>
        <el-table-column
          prop="merchantName"
          label="商家名称"
          min-width="120"
          show-overflow-tooltip>
        </el-table-column>
        <el-table-column
          prop="testCount"
          label="样品数量"
          min-width="80"
          align="center">
        </el-table-column>
        <el-table-column
          prop="deviceName"
          label="测试仪器"
          min-width="120"
          show-overflow-tooltip>
        </el-table-column>
        <el-table-column
          prop="createTime"
          label="创建时间"
          min-width="160"
          show-overflow-tooltip>
        </el-table-column>
        <el-table-column
          prop="expectedEndTime"
          label="预期完成时间"
          min-width="160"
          show-overflow-tooltip>
        </el-table-column>
        <!-- 添加超期天数列，仅在即将超期页面显示 -->
        <el-table-column
          v-if="currentOrderType === 'expiring'"
          label="超期情况"
          min-width="120"
          align="center">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.isExpired === 1 ? 'danger' : (scope.row.isExpiring === 1 ? 'warning' : 'info')"
              size="mini">
              {{ getExpiringDays(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          prop="orderStatus"
          label="订单状态"
          min-width="100"
          align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.orderStatus)">
              {{ getStatusText(scope.row.orderStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          min-width="180"
          align="center"
          fixed="right">
          <template slot-scope="scope">
            <div class="operation-buttons">
              <!-- 默认的查看详情按钮 -->
              <el-button 
                type="primary" 
                size="mini" 
                @click="handleViewDetail(scope.row)">查看详情</el-button>
              
              <!-- 未邮寄订单显示邮寄样品按钮 -->
              <el-button
                v-if="scope.row.orderStatus === 'NOT_SHIPPED'"
                type="success"
                size="mini"
                @click="handleShipSample(scope.row)">
                邮寄样品
              </el-button>
              
              <!-- 已测试订单显示提出争议/复测按钮 -->
              <el-button
                v-if="scope.row.orderStatus === 'TESTED' && scope.row.isDispute !== 1 && scope.row.isRetest !== 1 && scope.row.isBackOrder !== 1 && scope.row.isBackOrder !== -1 && scope.row.isForward !== 1 && scope.row.isForward !== -1"
                type="danger"
                size="mini"
                @click="handleDisputeOrRetest(scope.row)">
                提出争议/复测
              </el-button>
              
              <!-- 在途订单和已测试订单显示申请回寄/转寄按钮 -->
              <el-button
                v-if="(scope.row.orderStatus === 'IN_TRANSIT' || scope.row.orderStatus === 'TESTED') && scope.row.isBackOrder !== 1 && scope.row.isBackOrder !== -1 && scope.row.isForward !== 1 && scope.row.isForward !== -1"
                type="warning"
                size="mini"
                @click="handleRequestReturn(scope.row)">
                申请回寄
              </el-button>
              <el-button
                v-if="(scope.row.orderStatus === 'TESTED') && scope.row.isForward !== 1 && scope.row.isForward !== -1 && scope.row.isBackOrder !== 1 && scope.row.isBackOrder !== -1"
                type="info"
                size="mini"
                @click="handleRequestForward(scope.row)">
                申请转寄
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          background
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalItems">
        </el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import adminApi from '@/api/adminApi'

export default {
  name: 'OrderList',
  data() {
    return {
      filterForm: {
        orderSn: '',
        orderStatus: '',
        dateRange: []
      },
      tableLoading: false,
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      orderList: [],
      merchantOptions: [],
      // 订单状态选项
      statusOptions: {
        NOT_SHIPPED: '未邮寄',
        IN_TRANSIT: '在途',
        TESTING: '测试中',
        TESTED: '已测试',
        COMPLETED: '已完成',
        UNPAID: '未结算',
        RETURN: '回寄',
        FORWARD: '转寄',
        EXPIRING: '即将超期'
      },
      // 订单类型与状态映射
      orderTypeStatusMap: {
        all: '', // 所有订单
        'not-shipped': 'NOT_SHIPPED', // 未邮寄订单
        'in-transit': 'IN_TRANSIT', // 在途订单
        testing: 'TESTING', // 测试中订单
        tested: 'TESTED', // 已测试订单
        completed: 'COMPLETED', // 已完成订单
        expiring: 'EXPIRING', // 即将超期订单
        dispute: 'DISPUTE_RETEST', // 争议/复测订单
        return: 'RETURN_FORWARD', // 回寄/转寄订单
        unpaid: 'UNSETTLED', // 未结算订单
        cancel: 'CANCEL' // 取消订单
      },
      // 标题映射
      orderTypeTitleMap: {
        all: '订单列表',
        'not-shipped': '未邮寄订单列表',
        'in-transit': '在途订单列表',
        testing: '测试中订单列表',
        tested: '已测试订单列表',
        completed: '已完成订单列表',
        expiring: '即将/已超期订单列表',
        dispute: '争议/复测订单列表',
        return: '回寄/转寄订单列表'
      }
    }
  },
  computed: {
    // 根据路由获取当前订单类型
    currentOrderType() {
      const path = this.$route.path
      const orderType = path.split('/').pop() // 获取路径最后一段作为订单类型
      return orderType
    },
    // 获取当前订单状态
    currentOrderStatus() {
      return this.orderTypeStatusMap[this.currentOrderType] || ''
    },
    // 获取页面标题
    pageTitle() {
      return this.orderTypeTitleMap[this.currentOrderType] || '订单列表'
    }
  },
  watch: {
    // 监听路由变化，重新获取数据
    '$route.path'() {
      this.resetFilter()
      this.fetchOrders()
    }
  },
  created() {
    this.fetchOrders()
    this.fetchMerchantList()
  },
  methods: {
    // 获取商家列表
    async fetchMerchantList() {
      try {
        const res = await adminApi.getMerchantList()
        if (res && Array.isArray(res)) {
          this.merchantOptions = res
        } else {
          this.merchantOptions = []
        }
      } catch (error) {
        console.error('获取商家列表失败:', error)
        this.merchantOptions = []
      }
    },
    async fetchOrders() {
      this.tableLoading = true
      try {
        // 构建请求参数
        const params = {
          page: this.currentPage,
          limit: this.pageSize,
          orderSn: this.filterForm.orderSn || undefined,
        }

        // 处理订单状态参数
        if (this.currentOrderType === 'all') {
          // 所有订单页面，使用筛选条件中选择的状态
          params.orderStatus = this.filterForm.orderStatus || undefined
        } else if (this.currentOrderType === 'dispute') {
          // 争议/复测订单页面，使用特定状态集合
          params.isDispute = 1  // 使用争议标记来筛选
        } else {
          // 其他类型订单页面，如果筛选条件中有设置状态，优先使用筛选条件中的状态
          params.orderStatus = this.filterForm.orderStatus || this.currentOrderStatus || undefined
        }
        
        // 添加日期范围参数
        if (this.filterForm.dateRange && this.filterForm.dateRange.length === 2) {
          params.startDate = this.filterForm.dateRange[0]
          params.endDate = this.filterForm.dateRange[1]
        }
        
        const response = await adminApi.getOrderListByPage(params)
        
        if (response && response.data && response.data.code === 200) {
          this.orderList = response.data.data || []
          this.totalItems = response.data.count || 0
        } else {
          this.orderList = []
          this.totalItems = 0
          this.$message.error(response.msg || '获取订单列表失败')
        }
      } catch (error) {
        console.error('获取订单列表失败:', error)
        this.orderList = []
        this.totalItems = 0
        this.$message.error('获取订单列表失败')
      } finally {
        this.tableLoading = false
      }
    },
    handleFilter() {
      this.currentPage = 1
      this.fetchOrders()
    },
    resetFilter() {
      this.filterForm = {
        orderSn: '',
        orderStatus: '',
        dateRange: []
      }
      this.currentPage = 1
      this.pageSize = 10
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.fetchOrders()
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.fetchOrders()
    },
    // 获取状态类型（用于标签颜色）
    getStatusType(status) {
      const statusMap = {
        NOT_SHIPPED: 'info',
        IN_TRANSIT: 'primary',
        TESTING: 'warning',
        TESTED: 'success',
        COMPLETED: 'success',
        unpaid: 'success',
        RETURN: 'info',
        FORWARD: 'info',
        EXPIRING: 'danger',
        UNSETTLED: 'success',
        CANCEL: 'danger'
      }
      return statusMap[status] || 'info'
    },
    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        NOT_SHIPPED: '未邮寄',
        IN_TRANSIT: '在途',
        TESTING: '测试中',
        TESTED: '已测试',
        COMPLETED: '已完成',
        unpaid: '未结算',
        RETURN: '回寄',
        FORWARD: '转寄',
        EXPIRING: '即将超期',
        UNSETTLED: '已完成',
        CANCEL: '已取消'
      }
      return statusMap[status] || '未知状态'
    },
    handleViewDetail(row) {
      this.$router.push(`/customer/orders/detail/${row.id}`)
    },
    handleShipSample(row) {
      this.$confirm('确认要邮寄样品吗?', '邮寄样品', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(() => {
        // 跳转到管理端邮寄样品页面
        this.$router.push({
          path: `/customer/orders/ship/${row.id}`,
          query: {
            step: 'NOT_SHIPPED',
            action: 'ship'
          }
        })
      }).catch(() => {
        // 取消邮寄
      })
    },
    handleDisputeOrRetest(row) {
      this.$confirm('请选择申请类型', '提示', {
        confirmButtonText: '提出争议',
        cancelButtonText: '申请复测',
        type: 'warning',
        distinguishCancelAndClose: true,
        callback: (action) => {
          if (action === 'confirm') {
            // 提出争议 - 跳转到争议提交表单
            this.$router.push({
              path: `/customer/orders/management/${row.id}`,
              query: {
                step: 'SUBMIT_DISPUTE',
                action: 'dispute'
              }
            });
          } else if (action === 'cancel') {
            // 申请复测 - 跳转到复测上传页面
            this.$router.push({
              path: `/customer/orders/management/${row.id}`,
              query: {
                step: 'SUBMIT_RETEST',
                action: 'retest'
              }
            });
          }
        }
      })
    },
    handleViewReport(row) {
      this.$confirm('确认查看订单报告?', '查看报告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(() => {
        // 跳转到管理端报告页面
        this.$router.push({
          path: `/customer/orders/report/${row.id}`,
          query: {
            action: 'view_report'
          }
        })
      }).catch(() => {
        // 取消操作
      })
    },
    // 处理申请回寄或转寄
    handleRequestReturnOrForward(row) {
      this.$confirm('请选择申请类型', '提示', {
        confirmButtonText: '申请回寄',
        cancelButtonText: '申请转寄',
        type: 'warning',
        distinguishCancelAndClose: true,
        callback: (action) => {
          if (action === 'confirm') {
            // 申请回寄 - 跳转到回寄申请页面
            this.$router.push({
              path: `/customer/orders/return/${row.id}`
            });
          } else if (action === 'cancel') {
            // 申请转寄 - 跳转到转寄申请页面
            this.$router.push({
              path: `/customer/orders/forward/${row.id}`
            });
          }
        }
      })
    },
    // 处理申请回寄
    handleRequestReturn(row) {
      // 直接跳转到回寄申请页面
      this.$router.push({
        path: `/customer/orders/return/${row.id}`
      });
    },
    // 处理申请转寄
    handleRequestForward(row) {
      // 直接跳转到转寄申请页面
      this.$router.push({
        path: `/customer/orders/forward/${row.id}`
      });
    },
    getRowClassName({ row }) {
      // 调试输出行数据
      console.log('客户端处理行样式的订单数据:', row)
      console.log('isExpired值:', row.isExpired, '类型:', typeof row.isExpired)
      console.log('isExpiring值:', row.isExpiring, '类型:', typeof row.isExpiring)
      
      // 修改判断逻辑，同时兼容数字和字符串类型
      if (row.isExpired == 1) {
        console.log('应用已超期样式')
        return 'expired-row';
      } else if (row.isExpiring == 1) {
        console.log('应用即将超期样式')
        return 'expiring-row';
      }
      return '';
    },
    // 获取超期天数
    getExpiringDays(row) {
      const today = new Date();
      const endDate = new Date(row.expectedEndTime);
      
      if (row.isExpired === 1) {
        // 已超期：计算已超期天数
        const diffTime = Math.abs(today - endDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `已超期 ${diffDays} 天`;
      } else if (row.isExpiring === 1) {
        // 即将超期：计算剩余天数
        const diffTime = endDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `剩余 ${diffDays} 天`;
      }
      return '正常';
    }
  }
}
</script>

<style>
.orders-container {
  padding: 20px;
}
.filter-card {
  margin-bottom: 20px;
}
.filter-form {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
}
.filter-form .el-form-item {
  margin-right: 20px;
  margin-bottom: 10px;
}
.filter-form .el-form-item__label {
  font-weight: 500;
  color: #606266;
}
.form-buttons-container {
  display: flex;
  align-items: flex-start;
  margin-top: 4px;
}
.form-buttons .el-button {
  padding: 9px 20px;
  margin-left: 0;
  margin-right: 10px;
}
.form-buttons .el-button:last-child {
  margin-right: 0;
}
.table-card {
  margin-bottom: 20px;
}
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.header-left {
  display: flex;
  align-items: center;
}
.title {
  font-size: 16px;
  font-weight: bold;
  margin-right: 10px;
}
.count {
  color: #909399;
  font-size: 14px;
}
.pagination-container {
  margin-top: 20px;
  text-align: right;
}
.order-sn-cell {
  display: flex;
  align-items: center;
}

/* 表格样式优化 */
.el-table {
  font-size: 14px;
}
.el-table th {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 500;
  padding: 10px 0;
}
.el-table td {
  padding: 10px 0;
}
.el-table--striped .el-table__body tr.el-table__row--striped td {
  background-color: #fafafa;
}
.el-table__body tr.current-row>td {
  background-color: #ecf5ff !important;
}
.el-tag {
  font-size: 12px;
  padding: 0 6px;
  height: 22px;
  line-height: 20px;
}

/* 表格操作按钮 */
.operation-buttons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}

.operation-buttons .el-button {
  margin: 3px;
  padding: 6px 10px;
  font-size: 12px;
  border-radius: 4px;
}

.operation-buttons .el-button--primary {
  background-color: #409EFF;
  border-color: #409EFF;
  color: #fff;
}

.operation-buttons .el-button--success {
  background-color: #67C23A;
  border-color: #67C23A;
  color: #fff;
}

.operation-buttons .el-button--warning {
  background-color: #E6A23C;
  border-color: #E6A23C;
  color: #fff;
}

.operation-buttons .el-button--danger {
  background-color: #F56C6C;
  border-color: #F56C6C;
  color: #fff;
}

.operation-buttons .el-button--primary:hover,
.operation-buttons .el-button--primary:focus {
  background-color: #66b1ff;
  border-color: #66b1ff;
  color: #fff;
}

.operation-buttons .el-button--success:hover,
.operation-buttons .el-button--success:focus {
  background-color: #85ce61;
  border-color: #85ce61;
  color: #fff;
}

.operation-buttons .el-button--warning:hover,
.operation-buttons .el-button--warning:focus {
  background-color: #ebb563;
  border-color: #ebb563;
  color: #fff;
}

.operation-buttons .el-button--danger:hover,
.operation-buttons .el-button--danger:focus {
  background-color: #f78989;
  border-color: #f78989;
  color: #fff;
}

.expiring-row {
  color: #E6A23C !important;
}

.expiring-row td {
  color: #E6A23C !important;
}

.expired-row {
  color: #F56C6C !important;
}

.expired-row td {
  color: #F56C6C !important;
}
</style> 